import { useCallback, useEffect, useRef, useState } from "react";

export interface QueryState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Simple effect-based async fetcher.
 *
 *   const { data, error, loading, reload } = useQuery(
 *     async () => {
 *       const { data, error } = await supabase.from("v_client_list").select("*");
 *       if (error) throw new Error(error.message);
 *       return data as ClientRow[];
 *     },
 *     [],
 *   );
 *
 * The query re-runs whenever any value in `deps` changes, and on `reload()`.
 * Stale responses (superseded by a newer run or an unmount) are discarded.
 */
export function useQuery<T>(
  fn: () => Promise<T>,
  deps: unknown[],
): { data: T | null; error: string | null; loading: boolean; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  // Always call the latest fn without making it a dependency (callers pass
  // inline closures; deps[] is the explicit dependency contract).
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fnRef.current().then(
      (result) => {
        if (cancelled) return;
        setData(result);
        setError(null);
        setLoading(false);
      },
      (err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { data, error, loading, reload };
}

export default useQuery;
